import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '@/services/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import toast from 'react-hot-toast';
import { Settings, Save, Clock, Calendar, ShieldCheck, HelpCircle } from 'lucide-react';

export default function SystemSettingsPage() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Record<string, string>>({});

  const { data: settings = [], isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const res = await settingsApi.getAll();
      return res.data.data;
    },
  });

  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const updateMutation = useMutation({
    mutationFn: (data: any) => settingsApi.update(data),
    onSuccess: () => {
      toast.success('Settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || 'Failed to update settings');
    }
  });

  const handleChange = (key: string, value: string) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = (keys: string[]) => {
    const payload = keys.reduce((acc: any, key) => {
      acc[key] = formData[key];
      return acc;
    }, {});
    updateMutation.mutate(payload);
  };

  if (isLoading) return <div className="animate-pulse h-64 bg-slate-100 rounded-xl"></div>;

  return (
    <div className="space-y-6 max-w-4xl">

      <div className="space-y-8">
        
        {/* Organisation Settings */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-600" /> Organisation
            </h2>
            <Button size="sm" onClick={() => handleSave(['org_name', 'leave_year_start_month', 'leave_year_start_day', 'session_timeout_minutes'])} isLoading={updateMutation.isPending}>
              <Save className="w-4 h-4 mr-2" /> Save Section
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input 
              label="Organisation Name" 
              value={formData.org_name || ''} 
              onChange={(e) => handleChange('org_name', e.target.value)}
            />
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Leave Year Reset Date</label>
              <div className="flex gap-2">
                <Input 
                  type="number" 
                  min="1" max="31" 
                  placeholder="Day" 
                  className="w-20"
                  value={formData.leave_year_start_day || ''} 
                  onChange={(e) => handleChange('leave_year_start_day', e.target.value)}
                />
                <Input 
                  type="number" 
                  min="1" max="12" 
                  placeholder="Month" 
                  className="flex-1"
                  value={formData.leave_year_start_month || ''} 
                  onChange={(e) => handleChange('leave_year_start_month', e.target.value)}
                  helperText="e.g., 1 for January"
                />
              </div>
            </div>

            <Input 
              label="Session Timeout (Minutes)" 
              type="number" 
              value={formData.session_timeout_minutes || ''} 
              onChange={(e) => handleChange('session_timeout_minutes', e.target.value)}
              helperText="Auto-logout period of inactivity"
            />
          </div>
        </div>

        {/* Escalation Rules */}
        <div className="card p-6">
          <div className="flex items-center gap-3">
            <Clock className="w-5 h-5 text-amber-500" />
            <h2 className="text-lg font-bold text-slate-800">Escalation Timers</h2>
            <span className="ml-auto text-sm font-semibold text-slate-600 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">3 days (fixed)</span>
          </div>
          <p className="text-sm text-slate-500 mt-2">Applications auto-escalate after 3 days of no action.</p>
        </div>

        {/* System Rules */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-500" /> System Rules
            </h2>
            <Button size="sm" onClick={() => handleSave(['bench_risk_threshold_percentage', 'compoff_expiry_days', 'allow_post_facto_leave', 'handover_note_required'])} isLoading={updateMutation.isPending}>
              <Save className="w-4 h-4 mr-2" /> Save Section
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input 
              label="Bench Risk Threshold (%)" 
              type="number" 
              value={formData.bench_risk_threshold_percentage || ''} 
              onChange={(e) => handleChange('bench_risk_threshold_percentage', e.target.value)}
              helperText="Alerts when department absence exceeds this %"
            />
            <Input 
              label="Comp-Off Expiry (Days)" 
              type="number" 
              value={formData.compoff_expiry_days || ''} 
              onChange={(e) => handleChange('compoff_expiry_days', e.target.value)}
              helperText="Days before credited comp-off expires"
            />

            <div className="space-y-4 pt-2">
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input 
                  type="checkbox" 
                  checked={formData.allow_post_facto_leave === 'true'}
                  onChange={(e) => handleChange('allow_post_facto_leave', e.target.checked.toString())}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4" 
                />
                <span className="text-sm font-medium text-slate-700">Allow Post-Facto Leave Applications</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input 
                  type="checkbox" 
                  checked={formData.handover_note_required === 'true'}
                  onChange={(e) => handleChange('handover_note_required', e.target.checked.toString())}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 w-4 h-4" 
                />
                <span className="text-sm font-medium text-slate-700">Make Handover Note Mandatory (for long leaves)</span>
              </label>
            </div>
          </div>
        </div>

        {/* UI Settings */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-purple-500" /> UI & Onboarding
            </h2>
            <Button size="sm" onClick={() => handleSave(['current_tour_version'])} isLoading={updateMutation.isPending}>
              <Save className="w-4 h-4 mr-2" /> Save Section
            </Button>
          </div>
          
          <div className="flex items-center gap-4 bg-purple-50 p-4 rounded-xl border border-purple-100">
            <div className="flex-1">
              <h3 className="font-bold text-purple-900">Current Guided Tour Version: {formData.current_tour_version || '1'}</h3>
              <p className="text-sm text-purple-700 mt-1">Increment this version to force all users to see the guided tour again on their next login (useful after major UI updates).</p>
            </div>
            <Button 
              variant="secondary" 
              className="bg-white"
              onClick={() => {
                const current = parseInt(formData.current_tour_version || '1', 10);
                handleChange('current_tour_version', (current + 1).toString());
              }}
            >
              Increment Version
            </Button>
          </div>
        </div>

      </div>
    </div>
  );
}
